import React, { useState } from "react";
import { Box, Typography, Switch } from "@mui/material";

const FONT_FAMILY    = "'Public Sans', 'Montserrat', arial, sans-serif";
const LETTER_SPACING = "0.08em";

interface ConnectionRow {
  key: string;
  label: string;
  syncOn: boolean;
  autoHyperlinks: boolean;
}

const INITIAL_CONNECTIONS: ConnectionRow[] = [
  { key: "microsoft-copilot", label: "MICROSOFT COPILOT", syncOn: false, autoHyperlinks: false },
];

const ConnectionSettingsView: React.FC = () => {
  const [connections, setConnections] = useState<ConnectionRow[]>(INITIAL_CONNECTIONS);

  const toggle = (key: string, field: "syncOn" | "autoHyperlinks") => {
    setConnections(prev =>
      prev.map(c => {
        if (c.key !== key) return c;
        if (field === "syncOn") {
          const next = !c.syncOn;
          return { ...c, syncOn: next, autoHyperlinks: next ? c.autoHyperlinks : false };
        }
        return { ...c, [field]: !c[field] };
      })
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      {/* Header */}
      <Box sx={{
        border: "1px solid", borderColor: "divider", borderBottom: "none",
        px: 3, py: 2, bgcolor: "background.paper", flexShrink: 0,
        display: "flex", alignItems: "center",
      }}>
        <Typography sx={{
          fontWeight: 800, fontSize: "1.1rem", color: "text.primary",
          letterSpacing: LETTER_SPACING, fontFamily: FONT_FAMILY,
        }}>
          CONNECTION SETTINGS
        </Typography>
      </Box>

      {/* Card */}
      <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
        <Box sx={{
          border: "1px solid", borderColor: "divider",
          borderRadius: "10px", overflow: "hidden",
          bgcolor: "background.paper",
        }}>
          {connections.map((conn, idx) => (
            <Box
              key={conn.key}
              sx={{
                display: "flex", alignItems: "center",
                px: 3, py: 2,
                borderBottom: idx < connections.length - 1 ? "1px solid" : "none",
                borderColor: "divider",
                gap: 2, flexWrap: "wrap",
              }}
            >
              {/* Service name */}
              <Typography sx={{
                width: 200, flexShrink: 0,
                fontSize: "0.75rem", fontWeight: 600,
                color: "text.secondary", letterSpacing: LETTER_SPACING,
                fontFamily: FONT_FAMILY,
              }}>
                {conn.label}
              </Typography>

              {/* Turn Sync On */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Switch
                  size="small"
                  checked={conn.syncOn}
                  onChange={() => toggle(conn.key, "syncOn")}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: "#22c55e" },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#22c55e" },
                  }}
                />
                <Typography sx={{
                  fontSize: "0.8rem", color: "text.primary",
                  fontFamily: FONT_FAMILY, whiteSpace: "nowrap",
                }}>
                  Turn Sync On
                </Typography>
              </Box>

              {/* Automatically create Hyperlinks */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, opacity: conn.syncOn ? 1 : 0.4 }}>
                <Switch
                  size="small"
                  checked={conn.autoHyperlinks}
                  disabled={!conn.syncOn}
                  onChange={() => toggle(conn.key, "autoHyperlinks")}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: "#22c55e" },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#22c55e" },
                  }}
                />
                <Typography sx={{
                  fontSize: "0.8rem", color: "text.primary",
                  fontFamily: FONT_FAMILY, whiteSpace: "nowrap",
                }}>
                  Automatically create Hyperlinks
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default ConnectionSettingsView;
